import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Global admin → user configuration sync.
 *
 * Admin-controlled configuration tables are broadcast over realtime. This hook
 * listens once for the whole app and refreshes the matching cached queries so
 * open user sessions (desktop and mobile) reflect admin changes immediately,
 * without a page reload.
 *
 * Some legacy screens read configuration with direct one-off queries instead of
 * React Query; they can listen for the `CONFIG_CHANGED_EVENT` window event.
 */

export const CONFIG_CHANGED_EVENT = "acex:config-changed";

/** table → React Query key prefixes to invalidate */
const CONFIG_TABLE_KEYS: Record<string, string[][]> = {
  module_settings: [["module-settings"]],
  platform_settings: [["platform_settings"], ["branding"], ["pusher-config"]],
  fees_settings: [["fees"], ["fees-settings"]],
  fee_policies: [["fee-policies"], ["resolved-fee"]],
  fee_overrides: [["fee-overrides"], ["resolved-fee"]],
  limits_settings: [["limits"], ["limits-settings"]],
  landing_sections: [["landing_sections"]],
  token_configs: [["token-configs"]],
  fiat_currencies: [
    ["fiat-config"],
    ["fiat-currencies-active"],
    ["fiat-currencies-usd-rates"],
    ["fiat-exchange-rates"],
  ],
  kyc_verification_settings: [["kyc-verification-settings"]],
};

/** Query keys wiped whenever the session changes (login / logout). */
export const CONFIG_QUERY_KEYS: string[] = Array.from(
  new Set(Object.values(CONFIG_TABLE_KEYS).flat().map((k) => k[0]))
);

/** Polling fallback interval — used when realtime delivery is degraded. */
const CONFIG_POLL_MS = 20 * 1000;

export function useConfigRealtime() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refreshAll = () => {
      Object.values(CONFIG_TABLE_KEYS).flat().forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    };

    const channel = supabase.channel("global-config-sync");

    Object.entries(CONFIG_TABLE_KEYS).forEach(([table, keys]) => {
      channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table },
        () => {
          keys.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: key });
          });
          try {
            window.dispatchEvent(new CustomEvent(CONFIG_CHANGED_EVENT, { detail: { table } }));
          } catch {
            /* non-browser environment */
          }
        }
      );
    });

    channel.subscribe();

    // Fallback: realtime can be unavailable (blocked sockets, degraded CDC),
    // so configuration is also re-checked on a short interval and whenever the
    // tab regains focus or connectivity. Admin changes therefore always land
    // without the user reloading the app.
    const interval = window.setInterval(refreshAll, CONFIG_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshAll();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refreshAll);
    window.addEventListener("online", refreshAll);

    return () => {
      supabase.removeChannel(channel);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refreshAll);
      window.removeEventListener("online", refreshAll);
    };
  }, [queryClient]);
}

/** Clear every cached configuration query — used on sign in / sign out. */
export function invalidateConfigQueries(queryClient: ReturnType<typeof useQueryClient>) {
  CONFIG_QUERY_KEYS.forEach((key) => {
    queryClient.invalidateQueries({ queryKey: [key] });
  });
}
